import type { AdminSessionGroup, SessionEntry } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import { useState } from 'react';

import { STR } from '../../i18n';
import { adminSignOutSession, ApiError, listAdminSessions, signOutEverywhere } from '../../lib/api';
import { noteSignedOut } from '../../lib/bounce';
import { distinctApps } from '../../lib/devices';
import { initials, relativeTime } from '../../lib/format';
import { queryKeys } from '../../lib/queryKeys';
import { useSession } from '../../lib/session';
import { LOGIN_PATH } from '../../lib/surfaces';
import { showToast } from '../../lib/toast';
import { Modal, ModalParagraph, ModalProse } from '../../ui/Modal';
import { Callout, Chip, Skeleton } from '../../ui/Primitives';
import {
  cardStyle,
  detailPaneStyle,
  modalButtonStyle,
  rowActionStyle,
  searchFieldStyle,
} from '../../ui/styles';

import { AdminShell } from './AdminShell';

/**
 * The admin Sessions screen — reference/accounts-sessions-admin-demo.html, the chosen design
 * of record: a grouped accordion (person rows expanding to per-device rows), search that
 * narrows client-side (the endpoint takes no query parameter and returns the whole live set —
 * no pagination at this population), and Expand/Collapse all.
 *
 * Ordering is already correct from the server — groups by most-recent activity, and within a
 * group the current device first then last-active descending. This screen never re-sorts.
 *
 * THE CARVE-OUT: `apps` is empty on every row this wave (the plumbing ships; no consuming
 * application introspects yet). Nothing here may ever render "0 apps" — the four places that
 * matter are the group chip (hidden entirely at zero), the per-row chip (omitted), the meta
 * line's "last seen in" clause (omitted), and the header count (the apps segment dropped).
 */

type Dialog =
  | { kind: 'device'; group: AdminSessionGroup; session: SessionEntry }
  | { kind: 'all'; group: AdminSessionGroup };

const filterRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  padding: '10px 12px',
  borderBottom: '1px solid var(--border)',
};

const groupHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 13,
  padding: '13px 18px',
  flexWrap: 'wrap',
};

const groupToggleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 13,
  flex: 1,
  minWidth: 220,
  textAlign: 'left',
  borderRadius: 10,
  padding: '4px 8px 4px 4px',
  margin: '-4px -8px -4px -4px',
  cursor: 'pointer',
  border: '1px solid transparent',
  background: 'transparent',
  color: 'var(--text)',
};

const caretStyle: CSSProperties = {
  width: 18,
  height: 18,
  flex: 'none',
  display: 'grid',
  placeItems: 'center',
  color: 'var(--subtle)',
};

const groupActionStyle: CSSProperties = {
  padding: '8px 13px',
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 650,
  flex: 'none',
};

const devListStyle: CSSProperties = {
  borderTop: '1px solid var(--border)',
  background: 'var(--surface-2)',
};

const devRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '12px 18px 12px 65px',
  borderBottom: '1px solid var(--border)',
  flexWrap: 'wrap',
};

/** THE META LINE, admin form: `<address> · <location> · signed in <relative> · <relative
 *  last-active>`, with the app clause appended only per THE CARVE-OUT. */
function metaLine(session: SessionEntry): string {
  const parts: string[] = [];
  if (session.sourceAddress !== null) parts.push(session.sourceAddress);
  parts.push(
    session.location === null
      ? STR.sessions.locationUnknown
      : STR.sessions.locationApprox(session.location),
  );
  parts.push(STR.sessions.signedInRelative(relativeTime(session.signedInAt)));
  parts.push(relativeTime(session.lastActiveAt));
  const apps = distinctApps([session]);
  if (apps.length > 0) parts.push(STR.sessions.admin.lastSeenIn(apps.map((app) => app.name).join(', ')));
  return parts.join(' · ');
}

export function SessionsPage() {
  const queryClient = useQueryClient();
  const session = useSession();

  const [search, setSearch] = useState('');
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const sessionsQuery = useQuery({ queryKey: queryKeys.adminSessions, queryFn: listAdminSessions });
  const loading = sessionsQuery.isLoading;
  const groups = sessionsQuery.data?.groups ?? [];

  /*
   * THE COLOUR RIDES THE ROW (U2). Each app entry carries the application's own pill colour,
   * resolved server-side by the same id join that supplies its name — so this screen makes no
   * second read and holds no colour map. An application with no colour assigned arrives as
   * null and renders the neutral chip, which is the honest rendering of "no colour chosen".
   */

  const needle = search.trim().toLowerCase();
  const visibleGroups =
    needle === ''
      ? groups
      : groups.filter((group) =>
          `${group.user.name} ${group.user.email}`.toLowerCase().includes(needle),
        );

  const totalPeople = groups.length;
  const visiblePeopleCount = visibleGroups.length;
  const deviceTotal = visibleGroups.reduce((sum, group) => sum + group.sessions.length, 0);
  const appsTotal = distinctApps(visibleGroups.flatMap((group) => group.sessions)).length;
  const countText = loading
    ? ''
    : STR.sessions.admin.count(visiblePeopleCount, totalPeople, deviceTotal, appsTotal);

  const anyClosed = visibleGroups.some((group) => !openIds.has(group.user.id));

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminSessions });
    void queryClient.invalidateQueries({ queryKey: queryKeys.users });
  }

  function toggleGroup(userId: string) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  function onExpandCollapseAll() {
    setOpenIds((prev) => {
      const next = new Set(prev);
      for (const group of visibleGroups) {
        if (anyClosed) next.add(group.user.id);
        else next.delete(group.user.id);
      }
      return next;
    });
  }

  const deviceSignOutMutation = useMutation({
    mutationFn: (vars: { sessionId: string; name: string; deviceLabel: string }) =>
      adminSignOutSession(vars.sessionId),
    onSuccess: (_void, vars) => {
      refresh();
      setDialog(null);
      showToast(STR.toast.adminDeviceSignedOut(vars.name, vars.deviceLabel), 'warn');
    },
    onError: (error) => {
      setDialog(null);
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const allSignOutMutation = useMutation({
    mutationFn: (userId: string) => signOutEverywhere(userId),
    onSuccess: (result) => {
      // Ending your OWN current session kills the acting cookie too — leave the same way
      // logout does, via a full page load. See UsersPage's identical signOutMutation.
      if (session.data?.id === result.user.id) {
        noteSignedOut();
        window.location.assign(LOGIN_PATH);
        return;
      }
      refresh();
      setDialog(null);
      showToast(STR.toast.signedOutEverywhere(result.user.name), 'warn');
    },
    onError: (error) => {
      setDialog(null);
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  return (
    <AdminShell surfaceId="sessions">
      <div style={detailPaneStyle}>
        {/*
          `overflow: 'visible'` overrides `cardStyle`'s usual clip — deliberately, only here.
          The devices/apps tooltips are absolutely positioned popovers that can render below
          the last row; clipping them at the card's edge (as `cardStyle` does for every other
          admin list) would cut a short list's only tooltip off entirely. The trade-off is
          accepted: nothing inside this card paints a background that would show a squared
          corner escaping the 12px radius, except the expanded device list's `--surface-2`
          panel, which is one shade off the card's own `--surface` and negligible at a corner.
        */}
        <div style={{ ...cardStyle, width: '100%', overflow: 'visible' }}>
          <div style={filterRowStyle}>
            <input
              className="field"
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              placeholder={STR.sessions.admin.searchPlaceholder}
              aria-label={STR.sessions.admin.searchPlaceholder}
              style={{ ...searchFieldStyle, flex: 1, minWidth: 190, width: 'auto' }}
            />
            <button
              type="button"
              className="tbtn"
              disabled={loading || visibleGroups.length === 0}
              onClick={onExpandCollapseAll}
              style={{ marginLeft: 'auto', padding: '5px 10px', borderRadius: 7, fontSize: 12, fontWeight: 650 }}
            >
              {anyClosed ? STR.sessions.admin.expandAll : STR.sessions.admin.collapseAll}
            </button>
            {loading ? null : (
              <span
                className="tx-subtle"
                style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
              >
                {countText}
              </span>
            )}
          </div>

          {/*
            A dedicated list wrapper, exactly like the demo's `[data-list]` — separate from
            the filter row above, so the four mutually exclusive list states (skeleton,
            nobody-signed-in, no-match, the groups) swap as ONE unit without the filter row
            ever being part of that swap.

            No CSS rule keys off this element's child order. The tooltip in components.css is
            deliberately always-downward — never a flip-upward heuristic keyed on row position
            — because this screen's card renders `overflow: visible`, so a tooltip below the
            last row extends past the card instead of being clipped.
          */}
          <div>
            {loading ? <SessionsSkeleton /> : null}

            {!loading && totalPeople === 0 ? (
              <div style={{ padding: '56px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 15.5, fontWeight: 650 }}>{STR.sessions.admin.emptyTitle}</div>
                <p
                  className="tx-subtle"
                  style={{
                    maxWidth: 400,
                    margin: '8px auto 0',
                    fontSize: 13,
                    lineHeight: 1.6,
                    textWrap: 'pretty',
                  }}
                >
                  {STR.sessions.admin.emptyBody}
                </p>
              </div>
            ) : null}

            {!loading && totalPeople > 0 && visiblePeopleCount === 0 ? (
              <div style={{ padding: '34px 16px', textAlign: 'center' }}>
                <div className="tx-subtle" style={{ fontSize: 13 }}>
                  {STR.sessions.admin.noMatch}
                </div>
                <button
                  type="button"
                  className="tbtn"
                  onClick={() => {
                    setSearch('');
                  }}
                  style={{ ...rowActionStyle, marginTop: 7 }}
                >
                  {STR.common.clearSearch}
                </button>
              </div>
            ) : null}

            {!loading && visiblePeopleCount > 0
              ? visibleGroups.map((group) => (
                  <GroupRow
                    key={group.user.id}
                    group={group}
                    expanded={openIds.has(group.user.id)}
                    onToggle={() => {
                      toggleGroup(group.user.id);
                    }}
                    onSignOutDevice={(entry) => {
                      setDialog({ kind: 'device', group, session: entry });
                    }}
                    onSignOutAll={() => {
                      setDialog({ kind: 'all', group });
                    }}
                  />
                ))
              : null}
          </div>
        </div>
      </div>

      {dialog?.kind === 'device' ? (
        <Modal
          title={STR.sessions.admin.deviceModal.title(dialog.group.user.name)}
          onClose={() => {
            setDialog(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="ghost"
                style={modalButtonStyle}
                onClick={() => {
                  setDialog(null);
                }}
              >
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={deviceSignOutMutation.isPending}
                onClick={() => {
                  deviceSignOutMutation.mutate({
                    sessionId: dialog.session.id,
                    name: dialog.group.user.name,
                    deviceLabel: dialog.session.deviceLabel,
                  });
                }}
              >
                {STR.sessions.admin.deviceModal.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>
              {STR.sessions.admin.deviceModal.body(
                dialog.session.deviceLabel,
                dialog.session.sourceAddress,
                dialog.group.sessions.length - 1,
              )}
            </ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}

      {dialog?.kind === 'all' ? (
        <Modal
          title={STR.sessions.admin.allModal.title(dialog.group.user.name)}
          onClose={() => {
            setDialog(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="ghost"
                style={modalButtonStyle}
                onClick={() => {
                  setDialog(null);
                }}
              >
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={allSignOutMutation.isPending}
                onClick={() => {
                  allSignOutMutation.mutate(dialog.group.user.id);
                }}
              >
                {STR.sessions.admin.signOutOfAll}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>
              {STR.sessions.admin.allModal.body(STR.sessions.deviceWord(dialog.group.sessions.length))}
            </ModalParagraph>
            {session.data?.id === dialog.group.user.id ? (
              <Callout tone="attention" style={{ marginTop: 12, fontWeight: 600 }}>
                {STR.sessions.admin.allModal.selfNote}
              </Callout>
            ) : null}
          </ModalProse>
        </Modal>
      ) : null}
    </AdminShell>
  );
}

function GroupRow({
  group,
  expanded,
  onToggle,
  onSignOutDevice,
  onSignOutAll,
}: {
  group: AdminSessionGroup;
  expanded: boolean;
  onToggle: () => void;
  onSignOutDevice: (session: SessionEntry) => void;
  onSignOutAll: () => void;
}) {
  const deviceCount = group.sessions.length;
  const deviceWord = STR.sessions.deviceWord(deviceCount);
  const apps = distinctApps(group.sessions);
  const deviceNames = group.sessions.map((entry) =>
    STR.sessions.admin.deviceTooltipName(entry.deviceLabel, entry.current),
  );

  return (
    <div className="sess-group" style={{ borderBottom: '1px solid var(--border)' }}>
      <div style={groupHeadStyle}>
        <button
          type="button"
          className="hov"
          aria-expanded={expanded}
          aria-label={STR.sessions.admin.groupToggleLabel(group.user.name, deviceWord, expanded)}
          onClick={onToggle}
          style={groupToggleStyle}
        >
          <span aria-hidden="true" style={{ ...caretStyle, transform: expanded ? 'rotate(90deg)' : 'none' }}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M3 1.5 7 5 3 8.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="avatar" aria-hidden="true">
            {initials(group.user.name)}
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{group.user.name}</span>
            <span
              className="tx-subtle"
              style={{
                display: 'block',
                fontSize: 11.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {group.user.email}
            </span>
          </span>
          {/* THE CARVE-OUT — the group-level apps rollup chip is hidden entirely at zero,
              never rendered as "0 apps". */}
          {apps.length === 0 ? null : (
            <span className="tipwrap">
              <Chip
                tone="neutral"
                tabIndex={0}
                aria-label={STR.sessions.admin.appsAriaLabel(apps.map((app) => app.name).join(', '))}
              >
                {STR.sessions.admin.appWord(apps.length)}
              </Chip>
              <span className="tip" role="tooltip" aria-hidden="true">
                <span className="tl">{STR.sessions.admin.appsTooltipHeading}</span>
                <span style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                  {apps.map((app) => (
                    <Chip key={app.id} tone="neutral" swatch={app.color} size="sm">
                      {app.name}
                    </Chip>
                  ))}
                </span>
              </span>
            </span>
          )}
          <span className="tipwrap">
            <Chip
              tone="neutral"
              tabIndex={0}
              aria-label={STR.sessions.admin.devicesAriaLabel(deviceNames.join(', '))}
            >
              {deviceWord}
            </Chip>
            <span className="tip" role="tooltip" aria-hidden="true">
              <span className="tl">{STR.sessions.admin.devicesTooltipHeading}</span>
              {deviceNames.map((name, index) => (
                <span key={index} style={{ display: 'block', padding: '1px 0' }}>
                  {name}
                </span>
              ))}
            </span>
          </span>
        </button>
        <button type="button" className="ghost" style={groupActionStyle} onClick={onSignOutAll}>
          {STR.sessions.admin.signOutOfAll}
        </button>
      </div>

      {expanded ? (
        <div style={devListStyle}>
          {group.sessions.map((entry) => (
            <DeviceRow
              key={entry.id}
              session={entry}
              onSignOut={() => {
                onSignOutDevice(entry);
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DeviceRow({
  session,
  onSignOut,
}: {
  session: SessionEntry;
  onSignOut: () => void;
}) {
  const apps = distinctApps([session]);

  return (
    <div style={devRowStyle}>
      <div style={{ minWidth: 200, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className={session.deviceUnknown ? 'tx-subtle' : undefined}>{session.deviceLabel}</span>
          {/* THE CARVE-OUT — the per-row app chip is omitted entirely at zero. Where one
              does render it wears the application's own colour, read straight off the row
              (`apps[].color`), neutral when the application has none assigned. */}
          {apps.map((app) => (
            <Chip key={app.id} tone="neutral" swatch={app.color} size="sm">
              {app.name}
            </Chip>
          ))}
          {session.current ? <Chip tone="positive" size="sm">{STR.sessions.thisDevice}</Chip> : null}
        </div>
        <div className="tx-subtle" style={{ fontSize: 11.5, marginTop: 3, lineHeight: 1.55 }}>
          {metaLine(session)}
        </div>
      </div>
      {/* The acting admin's own current session has no per-row Sign out — ending it is the
          Security page's affordance; the server also refuses it with a 409. */}
      {session.current ? null : (
        <button type="button" className="ghost" style={rowActionStyle} onClick={onSignOut}>
          {STR.sessions.signOut}
        </button>
      )}
    </div>
  );
}

/** The screen's own first paint — the shape of the groups that are coming. */
function SessionsSkeleton() {
  return (
    <div aria-hidden="true">
      {[0, 1, 2, 3].map((row) => (
        <div key={row} style={{ ...groupHeadStyle, borderBottom: '1px solid var(--border)' }}>
          <Skeleton style={{ width: 34, height: 34, borderRadius: 999, flex: 'none' }} />
          <div style={{ flex: 1, minWidth: 180 }}>
            <Skeleton style={{ height: 12, width: 140, borderRadius: 5 }} />
            <Skeleton style={{ height: 9, width: 200, borderRadius: 5, marginTop: 7 }} />
          </div>
          <Skeleton style={{ height: 22, width: 76, borderRadius: 999 }} />
          <Skeleton style={{ height: 33, width: 108, borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );
}
